import { ICellRendererParams } from 'ag-grid-community';
import { TableV3ColDefs } from 'common/table';
import { TableV3FilterItem } from 'common/table/types/TableV3';
import { TableV3TruncatedList } from 'common/table/components/TableV3TruncatedList';
import {
  CustomFieldDisplay,
  CustomFieldType,
  OrganizationCustomFieldConfig
} from 'generated-graphql/hooks/apollo-hooks';
import { rangeFilterSettingsForCustomField } from 'modules/candidate-profile/tables/filters/buildSourcingSearchRequest';
import { getAgeInYears } from 'modules/candidate-profile/tables/maps/getAgeInYears';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import {
  CandidateSortField,
  toCustomFieldSortField
} from 'modules/candidate-profile/tables/sorting/candidateSort';
import { parseISO } from 'date-fns';
import { formatDate } from 'utils/datetime';

const BOOLEAN_FILTER_ITEMS: TableV3FilterItem[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const toFilterItems = (
  allowedValues?: (string | null)[] | null
): TableV3FilterItem[] =>
  (allowedValues ?? [])
    .filter((value): value is string => !!value)
    .map((value) => ({ label: value, value }));

const renderDate = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return '';

  try {
    return formatDate(parseISO(value));
  } catch {
    return value;
  }
};

// No sensible age (future, implausible or unparseable) falls back to the date as stored, so
// the cell shows what is there rather than a negative or absurd number.
const renderAge = (value: unknown): string => {
  const age = getAgeInYears(value);
  return age !== null ? String(age) : renderDate(value);
};

const renderCustomFieldCell =
  ({ type, displayAs }: OrganizationCustomFieldConfig) =>
  (
    params: ICellRendererParams<CandidateProfileTableColumns>
  ): React.ReactNode => {
    const { value } = params;
    if (Array.isArray(value)) {
      return <TableV3TruncatedList items={value as string[]} />;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (type === CustomFieldType.Date) {
      return displayAs === CustomFieldDisplay.Age
        ? renderAge(value)
        : renderDate(value);
    }
    return value != null ? String(value) : '';
  };

export const getCustomFieldColumns = (
  customFieldConfigs: OrganizationCustomFieldConfig[]
): TableV3ColDefs<CandidateProfileTableColumns, CandidateSortField> =>
  customFieldConfigs.map((config) => {
    const column: TableV3ColDefs<
      CandidateProfileTableColumns,
      CandidateSortField
    >[number] = {
      field: config.fieldKey,
      headerName: config.label,
      width: 220,
      sortField: toCustomFieldSortField(config.fieldKey),
      cellRenderer: renderCustomFieldCell(config)
    };

    const range = rangeFilterSettingsForCustomField(config);
    if (range) {
      column.rangeFilter = range;
      return column;
    }

    if (config.type === CustomFieldType.Boolean) {
      column.filterOptions = {
        items: BOOLEAN_FILTER_ITEMS,
        selectionType: 'single'
      };
      return column;
    }

    if (config.hasFixedOptions) {
      column.filterOptions = {
        items: toFilterItems(config.allowedValues),
        selectionType:
          config.type === CustomFieldType.TextArray ? 'multiple' : 'single',
        isSearchable: true
      };
      return column;
    }

    if (
      config.type === CustomFieldType.Text ||
      config.type === CustomFieldType.TextArray
    ) {
      column.searchable = true;
    }

    return column;
  });
